// FeatureRoute (#23) — garde de route par feature de forfait.
// Une page dont la feature n'est pas incluse affiche « Non inclus dans votre forfait » À LA PLACE
// de la page (plus de menu seulement masqué ni de redirection muette). Mêmes règles d'accès que
// PrivateRoute (connexion, mot de passe à réinitialiser, essai Free terminé → écran #20). Tant que le forfait n'est pas lu
// (`pending`), un indicateur d'attente est affiché : la page protégée n'est PAS montée (ses effets —
// appels de données d'une feature peut-être hors forfait — ne partent pas), et aucun refus n'est
// affiché par anticipation (relecture 1/2).
// L'interface n'est qu'un confort : la barrière qui fait foi est côté serveur (fns edge, enforce()).
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDashboard } from '../context/DashboardContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { TrialExpiredScreen, isAccessLocked } from './billing/TrialExpiredScreen';
import { t } from '../utils/translations';

// Même destination que planLimit.ts (BILLING_HREF, non exporté) : l'onglet facturation des réglages.
const BILLING_HREF = '/settings?tab=billing';
const DASHBOARD_HREF = '/menupb2b';

// Indicateur d'attente traduit par la clé EXISTANTE `loading` (15 langues) — relecture 2/2.
const Loading: React.FC = () => {
  const { language } = useDashboard();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100dvh', color: 'var(--gray-400)', fontSize: 14,
    }}>
      {t(language, 'loading')}
    </div>
  );
};

/** Écran « non inclus dans votre formule » : titre, explication, issue vers les forfaits, retour. */
export const FeatureNotInPlan: React.FC<{ feature: string }> = ({ feature }) => {
  const { language } = useDashboard();
  const navigate = useNavigate();
  const tr = (key: string) => t(language, key);
  return (
    <div
      role="alert"
      data-feature={feature}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '100dvh', padding: 24, textAlign: 'center', gap: 12,
      }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--pink-light)', color: 'var(--pink)',
      }}>
        <Lock size={26} aria-hidden="true" />
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{tr('featureGateTitle')}</h1>
      <p style={{ maxWidth: 480, margin: 0, color: 'var(--gray-600)', lineHeight: 1.5 }}>{tr('featureGateBody')}</p>
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" className="confirmation-modal-btn confirmation-modal-btn-primary" onClick={() => navigate(BILLING_HREF)}>
          {tr('planLimitSeePlans')}
        </button>
        <button type="button" className="confirmation-modal-btn confirmation-modal-btn-secondary" onClick={() => navigate(DASHBOARD_HREF)}>
          {tr('featureGateBack')}
        </button>
      </div>
    </div>
  );
};

export const FeatureRoute: React.FC<{ feature: string; element: React.ReactElement }> = ({ feature, element }) => {
  const { user, loading, activeAccess } = useAuth();
  const { can, pending } = useEntitlements();
  if (loading) return <Loading />;
  if (!user.loggedIn) return <Navigate to="/login" replace />;
  if (user.needsPasswordReset) return <Navigate to="/reset-password" replace />;
  if (isAccessLocked(user, activeAccess)) return <TrialExpiredScreen />;   // #20 : comme PrivateRoute
  if (pending) return <Loading />;               // forfait pas encore lu : ni la page, ni le refus
  if (can(feature)) return element;
  return <FeatureNotInPlan feature={feature} />;
};

export default FeatureRoute;
